import {
  CellReferenceLike,
  cellReferenceContainsCell,
  PdfCellMapPage,
  PdfSelectableCell,
  selectableCellIsInRange,
} from "./pdfCellSelection";

interface PdfCellSelectionOverlayOptions {
  pageMap: PdfCellMapPage;
  selectedRefs: CellReferenceLike[];
  onToggleCell: (cell: PdfSelectableCell) => void;
  onSelectRange: (
    start: PdfSelectableCell,
    end: PdfSelectableCell,
    availableCells: PdfSelectableCell[]
  ) => void;
}

export function createPdfCellSelectionOverlay({
  pageMap,
  selectedRefs,
  onToggleCell,
  onSelectRange,
}: PdfCellSelectionOverlayOptions): HTMLDivElement {
  const overlay = document.createElement("div");
  overlay.setAttribute("data-cell-selection-overlay", "true");
  overlay.style.cssText = `
    position: absolute;
    inset: 0;
    z-index: 6;
    pointer-events: none;
  `;

  const buttons = new Map<HTMLButtonElement, PdfSelectableCell>();
  let dragStart: PdfSelectableCell | null = null;
  let dragEnd: PdfSelectableCell | null = null;

  const paintButton = (
    button: HTMLButtonElement,
    cell: PdfSelectableCell,
    rangePreview = false
  ) => {
    const selected =
      rangePreview ||
      selectedRefs.some((ref) => cellReferenceContainsCell(ref, cell));
    const hasContent = cell.text.trim().length > 0;
    button.setAttribute("aria-pressed", selected ? "true" : "false");
    if (selected) {
      button.style.border = "2px solid #FF9900";
      button.style.background = "rgba(255, 153, 0, 0.32)";
    } else if (hasContent) {
      button.style.border = "1px solid rgba(37, 99, 235, 0.48)";
      button.style.background = "rgba(37, 99, 235, 0.08)";
    } else {
      button.style.border = "1px solid transparent";
      button.style.background = "transparent";
    }
  };

  const paintDragPreview = () => {
    buttons.forEach((cell, button) => {
      const inRange =
        dragStart !== null &&
        dragEnd !== null &&
        selectableCellIsInRange(cell, dragStart, dragEnd);
      paintButton(button, cell, inRange);
    });
  };

  pageMap.cells.forEach((cell) => {
    const [x0, y0, x1, y1] = cell.rect;
    const cellDescription = cell.text || "empty cell";
    const button = document.createElement("button");
    button.type = "button";
    button.title = `${cell.ref}: ${cellDescription}. Drag to select a range.`;
    button.setAttribute("aria-label", `Select ${cell.ref}: ${cellDescription}`);
    button.setAttribute("data-pdf-cell-ref", cell.ref);
    button.style.cssText = `
      position: absolute;
      left: ${(x0 / pageMap.width) * 100}%;
      top: ${(y0 / pageMap.height) * 100}%;
      width: ${((x1 - x0) / pageMap.width) * 100}%;
      height: ${((y1 - y0) / pageMap.height) * 100}%;
      box-sizing: border-box;
      padding: 0;
      margin: 0;
      cursor: pointer;
      pointer-events: auto;
      outline: none;
      touch-action: none;
      user-select: none;
    `;
    paintButton(button, cell);
    button.addEventListener("mouseenter", () => {
      if (!dragStart && button.getAttribute("aria-pressed") !== "true") {
        button.style.border = "1px solid rgba(37, 99, 235, 0.48)";
        button.style.background = "rgba(37, 99, 235, 0.22)";
      }
    });
    button.addEventListener("mouseleave", () => {
      if (!dragStart) paintButton(button, cell);
    });
    button.addEventListener("focus", () => {
      button.style.outline = "2px solid #2563EB";
      button.style.outlineOffset = "1px";
    });
    button.addEventListener("blur", () => {
      button.style.outline = "none";
    });
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.detail === 0) onToggleCell(cell);
    });
    buttons.set(button, cell);
    overlay.appendChild(button);
  });

  const cellAtPoint = (clientX: number, clientY: number) => {
    const element = document.elementFromPoint(clientX, clientY);
    const button = element?.closest<HTMLButtonElement>(
      "button[data-pdf-cell-ref]"
    );
    return button && overlay.contains(button)
      ? buttons.get(button) ?? null
      : null;
  };

  overlay.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-pdf-cell-ref]"
    );
    const cell = button ? buttons.get(button) : null;
    if (!cell) return;

    dragStart = cell;
    dragEnd = cell;
    overlay.setPointerCapture?.(event.pointerId);
    paintDragPreview();
    event.preventDefault();
    event.stopPropagation();
  });

  overlay.addEventListener("pointermove", (event) => {
    if (!dragStart) return;
    const hoveredCell = cellAtPoint(event.clientX, event.clientY);
    if (!hoveredCell || hoveredCell === dragEnd) return;
    dragEnd = hoveredCell;
    paintDragPreview();
    event.preventDefault();
  });

  const finishDrag = (event: PointerEvent, cancelled = false) => {
    if (!dragStart) return;
    const start = dragStart;
    const end =
      cellAtPoint(event.clientX, event.clientY) ?? dragEnd ?? dragStart;
    dragStart = null;
    dragEnd = null;
    if (overlay.hasPointerCapture?.(event.pointerId)) {
      overlay.releasePointerCapture?.(event.pointerId);
    }

    if (cancelled) {
      buttons.forEach((cell, button) => paintButton(button, cell));
    } else if (start.ref === end.ref && start.table === end.table) {
      onToggleCell(start);
    } else {
      onSelectRange(start, end, pageMap.cells);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  overlay.addEventListener("pointerup", (event) => finishDrag(event));
  overlay.addEventListener("pointercancel", (event) => finishDrag(event, true));
  return overlay;
}
